/**
 * ChargeProviderActions - Ações do provider na cobrança (QR Code PIX, Boleto, Link)
 * 
 * Exibe botões para copiar link de pagamento, ver QR Code PIX e linha digitável do boleto.
 * Visível quando a cobrança tem provider_charge_id vinculado.
 * Botões condicionais por status (desabilitados se pago/cancelado) e tipo de pagamento.
 * 
 * RELEASE: 2026-03-14 v1.0.0
 * RELEASE: 2026-03-15 v1.1.0 - Botões condicionais por status e billingType
 * RELEASE: 2026-06-27 v1.2.0 - Botão "Recibo" no header reaproveita link nativo
 *                              do provider (metadata.transaction_receipt_url),
 *                              alinhado ao botão exibido na coluna "Recibo" do
 *                              modal interno e no checkout público.
 * @author Módulo Financeiro - Detalhes de Cobrança
 */

import { useState } from 'react';
import { Copy, QrCode, FileText, ExternalLink, Loader2, CheckCircle2, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { FinanceiroStatus } from '@/hooks/useFinanceiroCobrancas';
// v1.1.0 (2026-06-21): resolução com contingência checkout transparente x provider.
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';

const logger = createLogger('ChargeProviderActions');

interface ChargeProviderActionsProps {
  /** ID do pagamento no provider (ex: pay_xxx do Asaas) */
  providerChargeId: string;
  /** ID da conta corrente vinculada */
  contaCorrenteId: string;
  /** URL de pagamento do provider (invoiceUrl) */
  providerInvoiceUrl?: string | null;
  /** Link de pagamento */
  providerPaymentLink?: string | null;
  /** Slug do provider (asaas, stripe...) */
  providerSlug?: string | null;
  /** Status atual da cobrança — desabilita botões de ação quando pago/cancelado */
  status?: FinanceiroStatus;
  /** Tipo de pagamento (pix, boleto, cartao_credito...) — controla visibilidade dos botões */
  billingType?: string | null;
  /** Tipo da cobrança (unica, parcelada) — esconde PIX/boleto no header para parceladas */
  tipoCobranca?: string | null;
  // Contingência de checkout (v1.1.0 - 2026-06-21)
  /** Modo preferido herdado da conta corrente. Default 'transparent'. */
  checkoutMode?: 'transparent' | 'provider' | null;
  /** Slug ativo da sessão de checkout transparente (cache em financeiro_cobrancas). */
  checkoutSessionSlug?: string | null;
  /**
   * v1.2.0 — Link nativo do recibo do provider (Asaas `transactionReceiptUrl`).
   * Mesmo link exibido na coluna "Recibo" do modal interno e no checkout público.
   * Quando presente, renderiza o botão "Visualizar recibo de pagamento".
   */
  receiptUrl?: string | null;
}

/**
 * Componente de ações do provider para cobranças integradas
 * Botões condicionais: PIX só aparece para tipo 'pix', Boleto só para tipo 'boleto'
 * Todos desabilitados quando status é 'pago' ou 'cancelado'
 */
export function ChargeProviderActions({
  providerChargeId,
  contaCorrenteId,
  providerInvoiceUrl,
  providerPaymentLink,
  providerSlug,
  status,
  billingType,
  tipoCobranca,
  checkoutMode,
  checkoutSessionSlug,
  receiptUrl,
}: ChargeProviderActionsProps) {
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixData, setPixData] = useState<{ encodedImage: string; payload: string } | null>(null);
  const [loadingPix, setLoadingPix] = useState(false);
  const [boletoData, setBoletoData] = useState<string | null>(null);
  const [loadingBoleto, setLoadingBoleto] = useState(false);
  const [copied, setCopied] = useState(false);

  // Contingência v1.1.0 (2026-06-21): link efetivo respeita modo da conta corrente.
  // Fallback automático caso preferido não esteja disponível.
  const resolvedLink = resolveCheckoutUrl({
    checkout_mode: checkoutMode,
    checkout_session_slug: checkoutSessionSlug,
    provider_invoice_url: providerInvoiceUrl,
    provider_payment_link: providerPaymentLink,
  });
  const checkoutUrl = resolvedLink.url;

  /** Cobrança finalizada — desabilita ações de pagamento */
  const isFinalizado = status === 'pago' || status === 'cancelado';

  /** Cobrança parcelada — PIX/boleto ficam por parcela, não no header */
  const isParcelada = tipoCobranca === 'parcelada';

  /** Determina se o botão PIX deve ser exibido (tipo PIX ou sem tipo definido, nunca para parcelada) */
  const showPix = !isParcelada && (!billingType || billingType === 'pix');

  /** Determina se o botão Boleto deve ser exibido (tipo boleto ou carnê, nunca para parcelada) */
  const showBoleto = !isParcelada && (billingType === 'boleto' || billingType === 'carne');

  /**
   * Copia texto para a área de transferência
   */
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copiado!`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  /**
   * Busca QR Code PIX do provider
   */
  const handleGetPixQrCode = async () => {
    setLoadingPix(true);
    try {
      const { data, error } = await supabase.functions.invoke('financial-charge', {
        body: {
          action: 'get_pix_qrcode',
          provider_charge_id: providerChargeId,
          conta_corrente_id: contaCorrenteId,
        },
      });

      if (error) throw error;

      if (data?.success && data?.encodedImage) {
        setPixData({
          encodedImage: data.encodedImage,
          payload: data.payload || '',
        });
        setPixDialogOpen(true);
      } else {
        toast.error(data?.error || 'QR Code PIX não disponível');
      }
    } catch (err) {
      logger.error('Erro ao buscar QR Code PIX', { error: err });
      toast.error('Erro ao buscar QR Code PIX');
    } finally {
      setLoadingPix(false);
    }
  };

  /**
   * Busca linha digitável do boleto
   */
  const handleGetBoleto = async () => {
    setLoadingBoleto(true);
    try {
      const { data, error } = await supabase.functions.invoke('financial-charge', {
        body: {
          action: 'get_boleto_url',
          provider_charge_id: providerChargeId,
          conta_corrente_id: contaCorrenteId,
        },
      });

      if (error) throw error;

      if (data?.success && data?.identificationField) {
        setBoletoData(data.identificationField);
        await copyToClipboard(data.identificationField, 'Linha digitável');
      } else {
        toast.error(data?.error || 'Boleto não disponível');
      }
    } catch (err) {
      logger.error('Erro ao buscar boleto', { error: err });
      toast.error('Erro ao buscar linha digitável');
    } finally {
      setLoadingBoleto(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1 flex-nowrap">
        {/* Link de pagamento — usa checkoutUrl (respeita modo + fallback automático) */}
        {checkoutUrl && (
          <Button
            variant="ghost"
            size="icon-sm"
            title={
              resolvedLink.origin === 'transparent'
                ? 'Copiar link do checkout transparente'
                : 'Copiar link do provider (contingência)'
            }
            onClick={() => copyToClipboard(checkoutUrl, 'Link de pagamento')}
          >
            {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        )}

        {/* Abrir link externamente — usa checkoutUrl resolvido */}
        {checkoutUrl && (
          <Button
            variant="ghost"
            size="icon-sm"
            title={
              resolvedLink.usedFallback
                ? 'Abrir link (contingência ativada)'
                : 'Abrir link de pagamento'
            }
            onClick={() => window.open(checkoutUrl, '_blank')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* v1.2.0 — Recibo do provider (link nativo, sem bucket). Renderizado
            apenas quando a cobrança está paga e o webhook já gravou o link. */}
        {receiptUrl && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300"
            title="Visualizar recibo de pagamento"
            onClick={() => window.open(receiptUrl, '_blank', 'noopener,noreferrer')}
          >
            <Receipt className="h-3.5 w-3.5" />
          </Button>
        )}


        {/* QR Code PIX — só aparece se billingType for 'pix' ou não definido */}
        {showPix && (
          <Button
            variant="ghost"
            size="icon-sm"
            title={isFinalizado ? 'Cobrança já finalizada' : 'Ver QR Code PIX'}
            onClick={handleGetPixQrCode}
            disabled={loadingPix || isFinalizado}
          >
            {loadingPix ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
          </Button>
        )}

        {/* Linha digitável boleto — só aparece se billingType for 'boleto' ou 'carne' */}
        {showBoleto && (
          <Button
            variant="ghost"
            size="icon-sm"
            title={isFinalizado ? 'Cobrança já finalizada' : 'Copiar linha digitável'}
            onClick={handleGetBoleto}
            disabled={loadingBoleto || isFinalizado}
          >
            {loadingBoleto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {/* Modal QR Code PIX — design Clean Executive (v2.0 — 2026-06-27) */}
      <Dialog open={pixDialogOpen} onOpenChange={setPixDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
          {/* Cabeçalho com ícone de marca PIX e título */}
          <DialogHeader className="flex-row items-center gap-3 space-y-0 px-6 sm:px-8 py-5 border-b border-border">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center shrink-0">
              <QrCode className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold tracking-tight">QR Code PIX</DialogTitle>
          </DialogHeader>

          <div className="px-6 sm:px-8 py-7 flex flex-col items-center">
            {/* Badge de status com pulso */}
            <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-full mb-7">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-widest">
                Aguardando pagamento
              </span>
            </div>

            {/* QR Code em moldura premium */}
            {pixData?.encodedImage && (
              <div className="relative p-4 bg-white rounded-2xl border-2 border-border shadow-sm">
                <img
                  src={`data:image/png;base64,${pixData.encodedImage}`}
                  alt="QR Code PIX"
                  className="w-[220px] h-[220px] rounded-lg"
                />
              </div>
            )}

            {/* Bloco de instrução */}
            <div className="w-full mt-7 p-4 bg-muted/50 rounded-2xl border border-border/60">
              <p className="text-sm text-center text-muted-foreground leading-relaxed">
                Abra o app do seu banco e{' '}
                <span className="font-bold text-foreground">escaneie o código</span> acima para pagar instantaneamente.
              </p>
            </div>

            {/* Campo copia e cola */}
            {pixData?.payload && (
              <div className="w-full mt-6">
                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2 block">
                  Código copia e cola
                </label>
                <div className="relative">
                  <div className="w-full bg-muted border border-border rounded-xl px-4 py-3 pr-14 text-xs font-mono text-muted-foreground break-all line-clamp-2">
                    {pixData.payload}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 bg-background shadow-sm hover:border-teal-500 hover:text-teal-600 transition-all"
                    onClick={() => copyToClipboard(pixData.payload, 'Código PIX')}
                    aria-label="Copiar código PIX"
                  >
                    {copied ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Botão fechar */}
            <Button
              onClick={() => setPixDialogOpen(false)}
              className="w-full mt-8 py-6 rounded-2xl font-semibold"
            >
              Concluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
